import * as Sentry from "@sentry/react";
import { action, autorun, makeAutoObservable, set, toJS } from "mobx";
import { atom } from "./atom";
import { PARENT_ORIGIN, SANDBOX_ORIGIN } from "./Constants";
import { CanvasSelection, SyncCanvasId, User } from "./Types";

export const canvasSelectionAtom = atom<CanvasSelection | undefined>(undefined);
export const syncCanvasIdAtom = atom<SyncCanvasId | undefined>(undefined);
export const userAtom = atom<User | undefined>(undefined);

type UIState = {
  maximizedPaneId: string | undefined;
};
export const uiStateMobx = makeAutoObservable<UIState>({
  maximizedPaneId: undefined,
});

let initializeSharedIframeState = () => {};

type IframeStateId = "CanvasSelection" | "SyncCanvasId" | "User" | "UI";

if (VITE_APP_IS_SANDBOX || window.__IS_SANDBOX === true) {
  function onMessage(message: MessageEvent) {
    switch (message.data.type) {
      case "SYNC_IFRAME_STATE": {
        const { stateId, value }: { stateId: IframeStateId; value: any } =
          message.data;
        switch (stateId) {
          case "CanvasSelection":
            // don't update canvasSelection. sandbox iframe will reload
            if (canvasSelectionAtom.value === undefined) {
              canvasSelectionAtom.value = value;
            }
            break;
          case "SyncCanvasId":
            syncCanvasIdAtom.value = value;
            break;
          case "User":
            userAtom.value = value;
            Sentry.setUser(
              value !== undefined
                ? {
                    id: value.id,
                    username: value.username,
                  }
                : null
            );
            break;
        }
        break;
      }
    }
  }
  window.addEventListener("message", action(onMessage));

  function syncIframeState(stateId: IframeStateId, value: any) {
    window.parent.postMessage(
      {
        type: "SYNC_IFRAME_STATE",
        stateId,
        value,
      },
      PARENT_ORIGIN
    );
  }
  autorun(() => {
    syncIframeState("UI", toJS(uiStateMobx));
  });
} else {
  initializeSharedIframeState = () => {
    const iframe = document.getElementById(
      "sandbox-iframe"
    ) as HTMLIFrameElement;
    function syncIframeState(stateId: IframeStateId, value: any) {
      iframe.contentWindow!.postMessage(
        {
          type: "SYNC_IFRAME_STATE",
          stateId,
          value,
        },
        SANDBOX_ORIGIN
      );
    }
    let unsubscribe: (() => void)[] = [];
    function initSandbox() {
      unsubscribe.forEach((unsub) => unsub());
      unsubscribe = [
        autorun(() => {
          syncIframeState("CanvasSelection", canvasSelectionAtom.value);
        }),
        autorun(() => {
          syncIframeState("SyncCanvasId", syncCanvasIdAtom.value);
        }),
        autorun(() => {
          syncIframeState("User", userAtom.value);
        }),
      ];
    }
    function onMessage(message: MessageEvent) {
      switch (message.data.type) {
        case "SANDBOX_INIT": {
          initSandbox();
          break;
        }
        case "SYNC_IFRAME_STATE": {
          const { stateId, value }: { stateId: IframeStateId; value: any } =
            message.data;
          switch (stateId) {
            case "UI":
              set(uiStateMobx, value);
              break;
          }
          break;
        }
      }
    }
    if (window.__DID_SANDBOX_INIT) {
      initSandbox();
    }
    window.addEventListener("message", action(onMessage));
  };
}

export { initializeSharedIframeState };
